import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Upload, File as FileIcon, AlertCircle, Image as ImageIcon, Film, Music, Clock } from 'lucide-react';
import { cn, formatBytes } from '@/lib/app-utils';

const FileUpload = ({ 
  onFileSelect, 
  onFilesSelect,
  multiple = false,
  accept = "*", 
  label = "Click or Drag to Upload", 
  maxSizeMB = 100, 
  totalSizeLimitMB = 200, 
  onUploadStatusChange
}) => {
  const [uploads, setUploads] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Notify parent about upload status (are we currently uploading?)
  useEffect(() => {
    const isUploading = uploads.some(u => u.status === 'uploading');
    if (onUploadStatusChange) {
      onUploadStatusChange(isUploading);
    }
  }, [uploads, onUploadStatusChange]);

  // Handle completion and handoff to parent
  useEffect(() => {
    // Identify files that are fully completed (100% and marked done)
    const completedItems = uploads.filter(u => u.status === 'completed');

    if (completedItems.length > 0) {
      const files = completedItems.map(u => u.file);

      // 1. Notify Parent
      if (multiple && onFilesSelect) {
        onFilesSelect(files);
      } else if (onFileSelect && files.length > 0) {
        onFileSelect(files[0]);
      }

      // 2. Remove completed files from internal state immediately to prevent duplication in UI.
      // The parent component (e.g., ProjectForm) will render them in its own list.
      const completedIds = completedItems.map(u => u.id);
      
      setUploads(prev => prev.filter(u => !completedIds.includes(u.id)));
    }
  }, [uploads, multiple, onFilesSelect, onFileSelect]);

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-blue-500" />;
    if (type.startsWith('video/')) return <Film className="w-5 h-5 text-purple-500" />;
    if (type.startsWith('audio/')) return <Music className="w-5 h-5 text-pink-500" />;
    return <FileIcon className="w-5 h-5 text-slate-500" />;
  };

  const validateFile = (file, currentUploads) => {
    // 1. Individual File Size
    if (file.size > maxSizeMB * 1024 * 1024) {
      return `File exceeds ${maxSizeMB}MB limit`;
    }
    
    // 2. Total Size Check
    const currentTotal = currentUploads.reduce((acc, u) => acc + u.file.size, 0);
    if ((currentTotal + file.size) > totalSizeLimitMB * 1024 * 1024) {
      return `Total upload size would exceed ${totalSizeLimitMB}MB`;
    }

    return null;
  };

  const startUpload = (newFiles) => {
    const newUploads = newFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'uploading', // uploading, completed, error
      error: null,
      speed: 0, 
      estimatedSeconds: 0
    }));

    // Filter and validate
    const validatedUploads = [];
    let tempUploadsState = [...uploads];

    for (const upload of newUploads) {
      const error = validateFile(upload.file, tempUploadsState);
      if (error) {
         validatedUploads.push({ ...upload, status: 'error', error, progress: 0 });
      } else {
         validatedUploads.push(upload);
         tempUploadsState.push(upload); 
      }
    }

    setUploads(prev => [...prev, ...validatedUploads]);

    // Simulate upload process
    validatedUploads.forEach(upload => {
      if (upload.status === 'error') return;

      let uploadedBytes = 0;

      const interval = setInterval(() => {
        // Variable speed simulation (0.5MB/s - 2MB/s)
        const speed = Math.random() * (2000000 - 500000) + 500000; 
        const chunk = speed * 0.2; // 200ms chunk
        
        uploadedBytes += chunk;
        let progress = Math.min((uploadedBytes / upload.file.size) * 100, 100);

        const remainingBytes = upload.file.size - uploadedBytes;
        const estimatedSeconds = speed > 0 ? remainingBytes / speed : 0;

        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          // Mark as completed - this will trigger the useEffect to handoff and clear
          setUploads(prev => prev.map(u => u.id === upload.id ? { 
            ...u, 
            status: 'completed', 
            progress: 100
          } : u));
        } else {
          setUploads(prev => prev.map(u => u.id === upload.id ? { 
            ...u, 
            progress,
            speed: speed,
            estimatedSeconds: Math.max(0, estimatedSeconds)
          } : u));
        }
      }, 200);
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    if (!multiple) {
      // For single file mode, we clear previous uploads first if any exist
      setUploads([]);
      startUpload([selectedFiles[0]]);
    } else {
      startUpload(selectedFiles);
    }
    
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length === 0) return;

    if (!multiple) {
      setUploads([]);
      startUpload([droppedFiles[0]]);
    } else {
      startUpload(droppedFiles);
    }
  };

  const handleRemove = (id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
  };

  const formatTime = (seconds) => {
    if (!seconds || seconds === Infinity) return 'Calculating...';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  // Filter out completed files from rendering to double-ensure no flicker/duplication
  const visibleUploads = uploads.filter(u => u.status !== 'completed');

  return (
    <div className="w-full space-y-4">
      {/* Drop Zone */}
      <div 
        className={cn(
          "relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ease-in-out group",
          isDragOver 
            ? "border-safety-orange bg-orange-50/50 scale-[1.005]" 
            : "border-slate-200 hover:border-safety-orange/50 hover:bg-slate-50",
          visibleUploads.length > 0 && !multiple ? "hidden" : "block"
        )}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input 
          type="file" 
          ref={fileInputRef}
          className="hidden" 
          accept={accept}
          multiple={multiple}
          onChange={handleFileChange}
        />
        <div className="flex flex-col items-center gap-4">
          <div className={cn(
            "p-4 rounded-full bg-white shadow-sm transition-transform duration-300 group-hover:scale-110", 
            isDragOver ? "text-safety-orange" : "text-slate-400"
          )}>
            <Upload className={cn("w-8 h-8", isDragOver && "animate-bounce")} />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-700">
              {isDragOver ? "Drop files now" : label}
            </h3>
            <div className="text-sm text-slate-500 space-y-1">
              <p>{multiple ? "Drag & drop multiple files" : "Drag & drop or click to browse"}</p>
              <p className="text-xs text-slate-400">
                Max {maxSizeMB}MB per file • Total limit {totalSizeLimitMB}MB
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload List (Only Active or Error) */}
      {visibleUploads.length > 0 && (
        <div className="space-y-3 animate-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center justify-between text-xs text-slate-500 px-1">
             <span>Uploading {visibleUploads.length} file{visibleUploads.length !== 1 ? 's' : ''}</span>
             <span>Total Size: {formatBytes(visibleUploads.reduce((acc, u) => acc + u.file.size, 0))}</span>
          </div>

          {visibleUploads.map((upload) => (
            <div 
              key={upload.id} 
              className={cn(
                "relative overflow-hidden border rounded-lg p-4 bg-white shadow-sm transition-all",
                upload.status === 'error' ? "border-red-200 bg-red-50/30" : "border-slate-200"
              )}
            >
              {/* Progress Bar Background */}
              {upload.status === 'uploading' && (
                <div 
                  className="absolute bottom-0 left-0 h-1 bg-blue-500/10 transition-all duration-200 ease-linear"
                  style={{ width: '100%' }}
                >
                   <div 
                      className="h-full bg-blue-500 transition-all duration-200 ease-linear"
                      style={{ width: `${upload.progress}%` }}
                   />
                </div>
              )}

              <div className="flex items-start gap-4 relative z-10">
                <div className={cn(
                  "p-2.5 rounded-lg shrink-0", 
                  upload.status === 'error' ? "bg-red-100" : "bg-blue-50"
                )}>
                   {upload.status === 'error' ? <X className="w-5 h-5 text-red-700" /> : getFileIcon(upload.file.type)}
                </div>
                
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 pr-4">
                      <p className="text-sm font-medium truncate text-slate-900">{upload.file.name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{formatBytes(upload.file.size)}</span>
                        {upload.status === 'uploading' && (
                           <>
                             <span className="w-1 h-1 rounded-full bg-slate-300" />
                             <span className="text-blue-600 font-medium">{Math.round(upload.progress)}%</span>
                           </>
                        )}
                      </div>
                    </div>
                    
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-6 w-6 text-slate-400 hover:text-red-600 hover:bg-red-50 -mt-1 -mr-2" 
                      onClick={() => handleRemove(upload.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Status & Speed Info */}
                  <div className="flex items-center justify-between text-xs pt-1">
                    {upload.status === 'uploading' && (
                      <div className="flex items-center gap-3 text-slate-500">
                        <span className="flex items-center gap-1">
                           <Upload className="w-3 h-3" /> {formatBytes(upload.speed)}/s
                        </span>
                        <span className="flex items-center gap-1">
                           <Clock className="w-3 h-3" /> {formatTime(upload.estimatedSeconds)} left
                        </span>
                      </div>
                    )}

                    {upload.status === 'error' && (
                      <span className="text-red-600 font-medium flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {upload.error}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;